/* eslint-disable no-return-assign */
import { chunk, mapValues, NumericDictionary } from 'lodash';

import * as dto from 'client/dto';
import { GeoShape, GeoshapeAugmentedVM, GeoshapeVM } from 'models/GeonameModels';
import { parseGeoShape } from 'utils/geonameHelper';
import mapHelper from 'utils/mapHelper';

import locationApi from './LocationApi';

const GeoshapeBulkCacheWindowProperty = 'BIOD_INSIGHTS_SHAPES';
type GeoshapeBulkCacheEntry = {
  GeonameId: number;
  LocationType: dto.LocationType;
  Name: string;
  FullDisplayName: string;
  Country: string;
  Province: string;
  Latitude: number;
  Longitude: number;
  Shape: GeoShape;
};
type GeonameShapeCacheBulkObject = NumericDictionary<GeoshapeBulkCacheEntry>;

let bulkCachePromiseSingleton: Promise<void> = null;
let GlobalSpoonfeedingIntervalRef = null;
const GeoshapeCache: { [key: number]: GeoshapeVM } = {};

/**
 * NOTE: function finishes when bulk country data is loaded into GeoshapeCache
 */
function loadBulkCountries(): Promise<void> {
  return (
    bulkCachePromiseSingleton ||
    (bulkCachePromiseSingleton = new Promise<GeonameShapeCacheBulkObject>((fnResolve, fnReject) => {
      if (window[GeoshapeBulkCacheWindowProperty]) {
        fnResolve(window[GeoshapeBulkCacheWindowProperty] as GeonameShapeCacheBulkObject);
        return;
      }
      // NOTE: this polling awaits the async loading of `shapes.js` (see index.html)
      const pollForBulkShapes = setInterval(() => {
        if (window[GeoshapeBulkCacheWindowProperty]) {
          fnResolve(window[GeoshapeBulkCacheWindowProperty] as GeonameShapeCacheBulkObject);
          clearInterval(pollForBulkShapes);
        }
      }, 100);
    }).then(data => {
      Object.assign(
        GeoshapeCache,
        mapValues(
          data,
          d =>
            ({
              geonameId: d.GeonameId,
              locationType: d.LocationType,
              locationName:
                d.LocationType === dto.LocationType.Country ? d.FullDisplayName : d.Name,
              locationContext:
                d.LocationType === dto.LocationType.Country
                  ? ''
                  : d.LocationType !== dto.LocationType.Province && d.Province
                  ? `${d.Province}, ${d.Country}`
                  : `${d.Country}`,
              x: d.Longitude,
              y: d.Latitude,
              shape: d.Shape,
              isPolygon: mapHelper.isPolygon(d.LocationType),
            } as GeoshapeVM)
        )
      );
    }))
  );
}

function getGeonameShapesWithCache(geonameIds: number[]): Promise<GeoshapeVM[]> {
  if (!geonameIds?.length) return Promise.resolve([]);
  return loadBulkCountries().then(() => {
    const cached = geonameIds.filter(id => !!GeoshapeCache[id]).map(id => GeoshapeCache[id]);
    if (cached.length === geonameIds.length) {
      return Promise.resolve(cached);
    }

    const geonameIdsToRequest =
      cached.length > 0 ? geonameIds.filter(id => !GeoshapeCache[id]) : geonameIds;

    return locationApi.getGeoname({ geonameIds: geonameIdsToRequest }).then(data => {
      for (const d of data) {
        GeoshapeCache[d.geonameId] = {
          geonameId: d.geonameId,
          locationType: d.locationType,
          locationName: d.locationType === dto.LocationType.Country ? d.fullDisplayName : d.name,
          locationContext:
            d.locationType === dto.LocationType.Country
              ? ''
              : d.locationType !== dto.LocationType.Province && d.province
              ? `${d.province}, ${d.country}`
              : `${d.country}`,
          x: d.longitude,
          y: d.latitude,
          shape: parseGeoShape(d.shape),
          isPolygon: mapHelper.isPolygon(d.locationType),
        };
      }
      const result = geonameIds.filter(id => !!GeoshapeCache[id]).map(id => GeoshapeCache[id]);
      return result;
    });
  });
}

function loadAndSpoonfeedEventLocationsGeoshapes(config: {
  locations: dto.EventLocationModel[];
  spoonfeedSize: number;
  spoonfeedDelay: number;
  requestChunkSizePoint: number;
  requestChunkSizePolygon: number;
  callback: (shapes: GeoshapeAugmentedVM<dto.EventLocationModel>[]) => void;
}) {
  const {
    locations,
    spoonfeedSize,
    spoonfeedDelay,
    requestChunkSizePoint,
    requestChunkSizePolygon,
    callback,
  } = config;

  const geoshapeQ = locations
    .filter(x => !!GeoshapeCache[x.geonameId])
    .map(x => GeoshapeCache[x.geonameId]);
  const locationsToRequest =
    geoshapeQ.length > 0 ? locations.filter(x => !GeoshapeCache[x.geonameId]) : locations;

  let isDoneLoading = locationsToRequest.length === 0;
  if (!isDoneLoading) {
    Promise.all([
      ...chunk(
        locationsToRequest.filter(x => x.locationType === dto.LocationType.City),
        requestChunkSizePoint
      )
        .map(page => page.map(x => x.geonameId))
        .map(pageIds =>
          getGeonameShapesWithCache(pageIds).then(data => {
            for (const d of data) geoshapeQ.push(d);
          })
        ),
      ...chunk(
        locationsToRequest.filter(x => mapHelper.isPolygon(x.locationType)),
        requestChunkSizePolygon
      )
        .map(page => page.map(x => x.geonameId))
        .map(pageIds =>
          getGeonameShapesWithCache(pageIds).then(data => {
            for (const d of data) geoshapeQ.push(d);
          })
        ),
    ]).then(() => {
      isDoneLoading = true;
    });
  }

  GlobalSpoonfeedingIntervalRef && clearInterval(GlobalSpoonfeedingIntervalRef);
  GlobalSpoonfeedingIntervalRef = setInterval(() => {
    const spoonful = geoshapeQ.splice(0, spoonfeedSize);
    if (spoonful.length > 0)
      callback(
        spoonful.map(
          x =>
            ({
              ...x,
              data: locations.find(e => e.geonameId === x.geonameId),
            } as GeoshapeAugmentedVM<dto.EventLocationModel>)
        )
      );
    if (spoonful.length === 0 && isDoneLoading) {
      clearInterval(GlobalSpoonfeedingIntervalRef);
      GlobalSpoonfeedingIntervalRef = null;
    }
  }, spoonfeedDelay);
}

export default {
  getGeonameShapesWithCache,
  loadAndSpoonfeedEventLocationsGeoshapes,
  loadBulkCountries,
};
